import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

// Validate required environment variables
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required');
}

if (!API_KEY) {
  throw new Error('REACT_APP_API_KEY environment variable is required');
}

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY
  }
});

class ReservationService {
  // Get all reservations with optional filters and pagination
  async getReservations(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.checkInDate) params.append('checkInDate', filters.checkInDate);
      if (filters.listingNickname) params.append('listingNickname', filters.listingNickname);
      if (filters.guestName) params.append('guestName', filters.guestName);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.lastKey) params.append('lastKey', JSON.stringify(filters.lastKey));

      const response = await apiClient.get(`/reservations?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching reservations:', error);
      throw this.handleError(error);
    }
  }

  // Get a single reservation by confirmation code
  async getReservation(confirmationCode) {
    try {
      const response = await apiClient.get(`/reservations/${confirmationCode}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching reservation:', error);
      throw this.handleError(error);
    }
  }

  // Create or update a reservation
  async createReservation(reservationData) {
    try {
      const response = await apiClient.put('/reservations', reservationData);
      return response.data;
    } catch (error) {
      console.error('Error creating reservation:', error);
      throw this.handleError(error);
    }
  }

  // Update a reservation (partial update)
  async updateReservation(confirmationCode, updates) {
    try {
      // For now, use PUT since we don't have PATCH implemented yet
      const existing = await this.getReservation(confirmationCode);
      const updatedData = { ...existing, ...updates };
      const response = await apiClient.put('/reservations', updatedData);
      return response.data;
    } catch (error) {
      console.error('Error updating reservation:', error);
      throw this.handleError(error);
    }
  }

  // Update reservation status (for guest information completion)
  async updateReservationStatus(confirmationCode, status, additionalData = {}) {
    try {
      const existing = await this.getReservation(confirmationCode);
      const updatedData = { 
        ...existing, 
        status,
        ...additionalData,
        updatedAt: new Date().toISOString()
      };
      const response = await apiClient.put('/reservations', updatedData);
      return response.data;
    } catch (error) {
      console.error('Error updating reservation status:', error);
      throw this.handleError(error);
    }
  }

  // Submit guest information
  async submitGuestInformation(confirmationCode, guestData) {
    try {
      const existing = await this.getReservation(confirmationCode);
      
      // Prepare the update with guest information
      const updatedData = {
        ...existing,
        status: 'Guest Information Provided',
        guestInformation: guestData.guestInformation || guestData,
        preferences: guestData.preferences || {},
        guestInfoCompletedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const response = await apiClient.put('/reservations', updatedData);
      return response.data;
    } catch (error) {
      console.error('Error submitting guest information:', error);
      throw this.handleError(error);
    }
  }

  // Partial save for guest information (new method)
  async savePartialGuestInformation(confirmationCode, partialData) {
    try {
      const existing = await this.getReservation(confirmationCode);
      
      // Prepare the partial update
      const updatedData = {
        ...existing,
        partialGuestInformation: partialData.guestInformation || [],
        preferences: partialData.preferences || {},
        partiallyCompleted: true,
        lastPartialSave: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const response = await apiClient.put('/reservations', updatedData);
      return response.data;
    } catch (error) {
      console.error('Error saving partial guest information:', error);
      throw this.handleError(error);
    }
  }

  // Generate guest information link
  generateGuestInfoLink(confirmationCode, language = 'en') {
    const baseUrl = window.location.origin;
    const langParam = language !== 'en' ? `?lang=${language}` : '';
    return `${baseUrl}/guest-info/${confirmationCode}${langParam}`;
  }

  // Check if guest information is required
  isGuestInfoRequired(reservation) {
    const requiredStatuses = ['Booking confirmed', 'Confirmed'];
    return requiredStatuses.includes(reservation.status) && 
           !reservation.guestInformation &&
           parseInt(reservation.numberOfAdults) > 0;
  }

  // Check if guest information is complete
  isGuestInfoComplete(reservation) {
    return reservation.status === 'Guest Information Provided' ||
           (reservation.guestInformation && reservation.guestInformation.length > 0);
  }

  // Delete a reservation (for now, update status to 'Cancelled')
  async deleteReservation(confirmationCode) {
    try {
      const existing = await this.getReservation(confirmationCode);
      const updatedData = { ...existing, status: 'Cancelled' };
      const response = await apiClient.put('/reservations', updatedData);
      return response.data;
    } catch (error) {
      console.error('Error deleting reservation:', error);
      throw this.handleError(error);
    }
  }

  // Search reservations by various criteria
  async searchReservations(searchTerm, searchType = 'guestName') {
    try {
      const filters = {};
      
      switch (searchType) {
        case 'guestName':
          // For now, get all and filter client-side since we don't have guest name index
          const allReservations = await this.getReservations({ limit: 1000 });
          const filtered = allReservations.reservations?.filter(r => 
            r.guestFirstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.guestLastName?.toLowerCase().includes(searchTerm.toLowerCase())
          ) || [];
          return { reservations: filtered, count: filtered.length };
        case 'listingNickname':
          filters.listingNickname = searchTerm;
          break;
        case 'checkInDate':
          filters.checkInDate = searchTerm;
          break;
        default:
          filters.guestName = searchTerm;
      }

      return await this.getReservations(filters);
    } catch (error) {
      console.error('Error searching reservations:', error);
      throw this.handleError(error);
    }
  }

  // Get reservation statistics for dashboard
  async getReservationStats() {
    try {
      const response = await this.getReservations({ limit: 1000 });
      
      const reservations = response.reservations || [];
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      
      const stats = {
        total: reservations.length,
        active: reservations.filter(r => r.status !== 'Deleted' && r.status !== 'Cancelled').length,
        checkingInToday: reservations.filter(r => r.checkInDate === today).length,
        checkingOutToday: reservations.filter(r => r.checkOutDate === today).length,
        byChannel: {},
        byStatus: {},
        recentReservations: reservations
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5)
      };

      // Calculate channel distribution
      reservations.forEach(reservation => {
        const channel = reservation.channel || 'Unknown';
        stats.byChannel[channel] = (stats.byChannel[channel] || 0) + 1;
      });

      // Calculate status distribution
      reservations.forEach(reservation => {
        const status = reservation.status || 'Unknown';
        stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error fetching reservation stats:', error);
      throw this.handleError(error);
    }
  }

  // Handle API errors
  handleError(error) {
    if (error.response) {
      // API responded with error status
      const message = error.response.data?.message || error.response.data?.error || 'API Error';
      return new Error(`${message} (${error.response.status})`);
    } else if (error.request) {
      // Network error
      return new Error('Network error - please check your connection');
    } else {
      // Other error
      return new Error(error.message || 'An unexpected error occurred');
    }
  }

  // Validate reservation data
  validateReservation(data, language = 'pt') {
    const errors = {};

    const messages = {
      pt: {
        confirmationCodeRequired: 'Código de confirmação é obrigatório',
        guestFirstNameRequired: 'Nome do hóspede é obrigatório',
        guestLastNameRequired: 'Sobrenome do hóspede é obrigatório',
        checkInDateRequired: 'Data de check-in é obrigatória',
        checkOutDateRequired: 'Data de check-out é obrigatória',
        checkOutAfterCheckIn: 'Data de check-out deve ser posterior à data de check-in',
        propertyRequired: 'Propriedade é obrigatória',
        channelRequired: 'Canal de reserva é obrigatório',
        numberOfGuestsRequired: 'Número de hóspedes é obrigatório',
        invalidPhoneNumber: 'Número de telefone inválido'
      },
      en: {
        confirmationCodeRequired: 'Confirmation code is required',
        guestFirstNameRequired: 'Guest first name is required',
        guestLastNameRequired: 'Guest last name is required',
        checkInDateRequired: 'Check-in date is required',
        checkOutDateRequired: 'Check-out date is required',
        checkOutAfterCheckIn: 'Check-out date must be after check-in date',
        propertyRequired: 'Property is required',
        channelRequired: 'Booking channel is required',
        numberOfGuestsRequired: 'Number of guests is required',
        invalidPhoneNumber: 'Invalid phone number'
      }
    };

    const t = (key) => messages[language]?.[key] || messages.en[key] || key;

    if (!data.confirmationCode?.trim()) {
      errors.confirmationCode = t('confirmationCodeRequired');
    }

    if (!data.guestFirstName?.trim()) {
      errors.guestFirstName = t('guestFirstNameRequired');
    }

    if (!data.guestLastName?.trim()) {
      errors.guestLastName = t('guestLastNameRequired');
    }

    if (!data.checkInDate) {
      errors.checkInDate = t('checkInDateRequired');
    }

    if (!data.checkOutDate) {
      errors.checkOutDate = t('checkOutDateRequired');
    }

    if (data.checkInDate && data.checkOutDate && new Date(data.checkInDate) >= new Date(data.checkOutDate)) {
      errors.checkOutDate = t('checkOutAfterCheckIn');
    }

    if (!data.listingNickname?.trim()) {
      errors.listingNickname = t('propertyRequired');
    }

    if (!data.channel?.trim()) {
      errors.channel = t('channelRequired');
    }

    if (!data.numberOfGuests || data.numberOfGuests < 1) {
      errors.numberOfGuests = t('numberOfGuestsRequired');
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
}

export default new ReservationService();
